<template>
  <div class="ccy">
    <button type="button" class="ccy__pill" :data-testid="btnTestid" @click="toggle">
      {{ modelValue }}
      <IconChevronDown :size="14" />
    </button>
    <template v-if="open">
      <div class="ccy__backdrop" @click="open = false" />
      <div class="ccy__menu">
        <input
          ref="search"
          v-model="q"
          class="ccy__search"
          :data-testid="searchTestid"
          :placeholder="t('vitrine.topup.ccySearch')"
          @keydown.enter.prevent="onEnter"
        />
        <div class="ccy__list">
          <button
            v-for="c in filtered"
            :key="c"
            type="button"
            class="ccy__opt"
            :class="{ 'ccy__opt--on': c === modelValue }"
            @click="pick(c)"
          >{{ c }}</button>
          <button v-if="custom" type="button" class="ccy__opt ccy__opt--custom" @click="pick(qq)">
            {{ t('vitrine.topup.ccyChoose', { code: qq }) }}
          </button>
          <div v-if="!filtered.length && !custom" class="ccy__empty">{{ t('vitrine.topup.ccyEmpty') }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconChevronDown } from '@/components/ui/icons';

const props = defineProps<{
  modelValue: string;
  /** Suggested codes to offer (fed from the reference API). Custom codes are still accepted by typing. */
  suggestions: string[];
  btnTestid?: string;
  searchTestid?: string;
}>();
const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>();

const { t } = useI18n();
const open = ref(false);
const q = ref('');
const search = ref<HTMLInputElement | null>(null);

const qq = computed(() => q.value.trim().toUpperCase());
const filtered = computed(() => (qq.value ? props.suggestions.filter(c => c.startsWith(qq.value)) : props.suggestions));
const custom = computed(() => qq.value.length >= 2 && !props.suggestions.includes(qq.value));

function toggle() {
  open.value = !open.value;
  if (open.value) nextTick(() => search.value?.focus());
}
function pick(c: string) { emit('update:modelValue', c); open.value = false; q.value = ''; }
function onEnter() {
  if (custom.value) pick(qq.value);
  else if (filtered.value[0]) pick(filtered.value[0]);
}
</script>

<style scoped lang="scss">
.ccy { position: relative; flex-shrink: 0; }
.ccy__pill {
  display: flex; align-items: center; gap: 5px;
  padding: 7px 12px; border-radius: 100px; cursor: pointer; font-family: inherit;
  background: rgba(var(--accent-rgb), 0.10); border: 1px solid var(--hairline-strong);
  color: var(--gold-light); font-size: 15px; font-weight: 600;
}
.ccy__backdrop { position: fixed; inset: 0; z-index: 40; }
.ccy__menu {
  position: absolute; right: 0; top: calc(100% + 6px); z-index: 41; width: 168px;
  border-radius: 14px; overflow: hidden;
  background: var(--bg-card); border: 1px solid var(--hairline-strong);
  box-shadow: var(--shadow-pop, 0 16px 40px rgba(0, 0, 0, 0.18));
}
.ccy__search {
  width: 100%; box-sizing: border-box; padding: 10px 13px;
  background: transparent; border: none; outline: none;
  border-bottom: 1px solid var(--hairline);
  color: var(--text); font-size: 12.5px; font-family: inherit;
}
.ccy__list { max-height: 194px; overflow-y: auto; }
.ccy__opt {
  display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 9px 13px; background: none; border: none;
  color: var(--text); font-size: 13px; font-family: inherit;
}
.ccy__opt--on { background: rgba(var(--accent-rgb), 0.10); color: var(--gold-light); font-weight: 600; }
.ccy__opt--custom { color: var(--gold); font-weight: 600; border-top: 1px solid var(--hairline); }
.ccy__empty { padding: 9px 13px; font-size: 12px; color: var(--text-3); }
</style>
